import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import NavbarTrungThu from "../Layout/NavbarTrungThu";
import CakeKpiSettings from "./CakeKpiSettings";
import {
  CAKE_BRANCHES,
  DONG_TIEN_CAKE_STORE_IDS,
  fetchBonpasCakeDailySales,
  fetchBonpasCakeSummary,
  fetchCakeCategorySales,
  fetchCakeDailySales,
  fetchCakeInventory,
  fetchCakeKpis,
  fetchCakeOrderSummary,
  fetchCakeProduction,
  fetchCakeRetailSummary,
  fetchLargeCakeTopStores,
  unwrapCakeKpiRows,
} from "./cakeDashboardApi";
import {
  aggregateKpiRows,
  enumerateDateKeys,
  filterInventoryByStores,
  formatBonpasStoreLabel,
  safePercentage,
  shiftDateKey,
  sumBy,
  toDateKey,
} from "./cakeDashboardUtils";
import "./Dashboard-data-bkem.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const EMPTY_DASHBOARD = {
  retail: null,
  orders: null,
  daily: null,
  seasonDaily: null,
  topStores: [],
  inventory: null,
  production: null,
  productionSeason: null,
  categorySales: [],
  bonpasPeriod: [],
  bonpasDaily: [],
  bonpasSeason: [],
  bonpasCategorySales: [],
  bonpasTopStores: [],
};

const numberFormat = new Intl.NumberFormat("vi-VN");
const FILTER_MODES = Object.freeze({ DATE_RANGE: "DATE_RANGE", SEASON: "SEASON" });

const CircleMetric = ({ eyebrow, title, value, target, color, note }) => {
  const percentage = safePercentage(value, target);
  const numericValue = Number(value || 0);
  const numericTarget = Number(target || 0);
  const chartValue = numericTarget > 0
    ? Math.min(numericValue, numericTarget)
    : Math.max(numericValue, 1);
  const chartData = {
    labels: numericTarget > 0 ? ["Đã đạt", "Còn lại"] : ["Số lượng"],
    datasets: [
      {
        data: numericTarget > 0
          ? [chartValue, Math.max(numericTarget - chartValue, 0)]
          : [chartValue],
        backgroundColor: numericTarget > 0
          ? [color, "#e9edf5"]
          : [color],
        borderWidth: 0,
      },
    ],
  };
  return (
    <article className="cake-chart-card cake-circle-card">
      <div className="cake-chart-heading">
        <div>
          <p className="cake-eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
      </div>
      <div className="cake-circle-chart">
        <Doughnut
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: "76%",
            plugins: { legend: { display: false } },
          }}
        />
        <div className="cake-circle-value">
          <strong>{numberFormat.format(numericValue)}</strong>
          <span>{percentage == null ? "cái" : `${percentage}%`}</span>
        </div>
      </div>
      <p className="cake-circle-note">
        {numericTarget > 0
          ? `KPI ${numberFormat.format(numericTarget)} cái · Còn ${numberFormat.format(Math.max(numericTarget - numericValue, 0))}`
          : note || "Số thực tế, chưa áp KPI"}
      </p>
    </article>
  );
};

const DashboardBkemCustomByMockData = () => {
  const today = toDateKey();
  const [selectedBranch, setSelectedBranch] = useState(CAKE_BRANCHES.ALL.code);
  const [selectedStartDate, setSelectedStartDate] = useState(today);
  const [selectedEndDate, setSelectedEndDate] = useState(today);
  const [filterMode, setFilterMode] = useState(FILTER_MODES.DATE_RANGE);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [seasonConfigurations, setSeasonConfigurations] = useState([]);
  const [kpiRows, setKpiRows] = useState([]);
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const canManageKpi =
    localStorage.getItem("isAdmin") === "true" ||
    localStorage.getItem("isBakery") === "true";

  const selectedStoreIds = DONG_TIEN_CAKE_STORE_IDS;

  useEffect(() => {
    let active = true;
    fetchCakeKpis()
      .then((result) => {
        if (active) setSeasonConfigurations(unwrapCakeKpiRows(result));
      })
      .catch(() => {
        if (active) setSeasonConfigurations([]);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const availableSeasons = useMemo(
    () =>
      seasonConfigurations.filter(
        (configuration) =>
          selectedBranch === CAKE_BRANCHES.ALL.code ||
          configuration.ChiNhanh === selectedBranch
      ),
    [seasonConfigurations, selectedBranch]
  );

  const handleBranchChange = (branch) => {
    setSelectedBranch(branch);
    if (filterMode === FILTER_MODES.SEASON) setSelectedSeasonId("");
  };

  const handleSeasonChange = (seasonId) => {
    setSelectedSeasonId(seasonId);
    const season = seasonConfigurations.find(
      (configuration) => String(configuration.ID) === String(seasonId)
    );
    if (!season) return;
    setSelectedStartDate(season.NgayBatDau);
    setSelectedEndDate(season.NgayKetThuc);
    if (selectedBranch === CAKE_BRANCHES.ALL.code) {
      setSelectedBranch(season.ChiNhanh);
    }
  };

  const loadDashboard = useCallback(async () => {
    if (filterMode === FILTER_MODES.SEASON && !selectedSeasonId) return;
    if (selectedStartDate > selectedEndDate) return;

    setLoading(true);
    setError("");
    try {
      const branch = selectedBranch === CAKE_BRANCHES.ALL.code ? undefined : selectedBranch;
      const kpiResult = await fetchCakeKpis({ branch, date: selectedEndDate });
      const nextKpiRows = unwrapCakeKpiRows(kpiResult);
      const kpiSummary = aggregateKpiRows(nextKpiRows, selectedEndDate);
      const trendStartDate = shiftDateKey(selectedEndDate, -6);
      const periodBody = {
        MaCH: selectedStoreIds,
        NgayBatDau: selectedStartDate,
        NgayKetThuc: selectedEndDate,
      };
      const trendBody = {
        MaCH: selectedStoreIds,
        NgayBatDau: trendStartDate,
        NgayKetThuc: selectedEndDate,
      };
      const seasonBody = kpiSummary.startDate
        ? {
            MaCH: selectedStoreIds,
            NgayBatDau: kpiSummary.startDate,
            NgayKetThuc: kpiSummary.endDate,
          }
        : null;
      const loadDongTien = selectedBranch !== CAKE_BRANCHES.BONPAS.code;
      const loadBonpas = selectedBranch !== CAKE_BRANCHES.DONG_TIEN.code;

      const [
        retail,
        orders,
        daily,
        seasonDaily,
        topStores,
        inventory,
        production,
        productionSeason,
        bonpasPeriod,
        bonpasDaily,
        bonpasSeason,
        categorySales,
        bonpasSummary,
      ] =
        await Promise.all([
          loadDongTien ? fetchCakeRetailSummary(periodBody) : Promise.resolve(null),
          loadDongTien ? fetchCakeOrderSummary(periodBody) : Promise.resolve(null),
          loadDongTien ? fetchCakeDailySales(trendBody) : Promise.resolve(null),
          loadDongTien && seasonBody
            ? fetchCakeDailySales(seasonBody)
            : Promise.resolve(null),
          loadDongTien ? fetchLargeCakeTopStores(periodBody) : Promise.resolve([]),
          loadDongTien ? fetchCakeInventory() : Promise.resolve(null),
          loadDongTien
            ? fetchCakeProduction({
                startDate: selectedStartDate,
                endDate: selectedEndDate,
              })
            : Promise.resolve(null),
          loadDongTien && seasonBody
            ? fetchCakeProduction({
                startDate: seasonBody.NgayBatDau,
                endDate: seasonBody.NgayKetThuc,
              })
            : Promise.resolve(null),
          loadBonpas
            ? fetchBonpasCakeDailySales({
                startDate: selectedStartDate,
                endDate: selectedEndDate,
              })
            : Promise.resolve({ data: [] }),
          loadBonpas
            ? fetchBonpasCakeDailySales({
                startDate: trendStartDate,
                endDate: selectedEndDate,
              })
            : Promise.resolve({ data: [] }),
          loadBonpas && seasonBody
            ? fetchBonpasCakeDailySales({
                startDate: seasonBody.NgayBatDau,
                endDate: seasonBody.NgayKetThuc,
              })
            : Promise.resolve({ data: [] }),
          loadDongTien
            ? fetchCakeCategorySales(periodBody)
            : Promise.resolve({ data: [] }),
          loadBonpas
            ? fetchBonpasCakeSummary({
                startDate: selectedStartDate,
                endDate: selectedEndDate,
              })
            : Promise.resolve({ data: {} }),
        ]);

      setKpiRows(nextKpiRows);
      setDashboard({
        retail,
        orders,
        daily,
        seasonDaily,
        topStores: Array.isArray(topStores) ? topStores : [],
        inventory,
        production,
        productionSeason,
        bonpasPeriod: Array.isArray(bonpasPeriod?.data) ? bonpasPeriod.data : [],
        bonpasDaily: Array.isArray(bonpasDaily?.data) ? bonpasDaily.data : [],
        bonpasSeason: Array.isArray(bonpasSeason?.data) ? bonpasSeason.data : [],
        categorySales: Array.isArray(categorySales?.data) ? categorySales.data : [],
        bonpasCategorySales: Array.isArray(bonpasSummary?.data?.categorySales)
          ? bonpasSummary.data.categorySales
          : [],
        bonpasTopStores: Array.isArray(bonpasSummary?.data?.topStores)
          ? bonpasSummary.data.topStores.map((store) => ({
              ...store,
              ChiNhanh: CAKE_BRANCHES.BONPAS.code,
            }))
          : [],
      });
    } catch (loadError) {
      setError(loadError.message || "Không thể tải dashboard bánh kem.");
    } finally {
      setLoading(false);
    }
  }, [
    filterMode,
    selectedBranch,
    selectedEndDate,
    selectedSeasonId,
    selectedStartDate,
    selectedStoreIds,
  ]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, refreshKey]);

  const kpiSummary = useMemo(
    () => aggregateKpiRows(kpiRows, selectedEndDate),
    [kpiRows, selectedEndDate]
  );
  const inventorySummary = useMemo(
    () => filterInventoryByStores(dashboard.inventory, selectedStoreIds),
    [dashboard.inventory, selectedStoreIds]
  );
  const daySales = useMemo(
    () => {
      const dongTien =
        (dashboard.daily?.BanLe || []).find((row) => row.Ngay === selectedEndDate)
          ?.ChiTiet?.BanhKemLon || 0;
      const bonpas =
        dashboard.bonpasDaily.find((row) => row.Ngay === selectedEndDate)
          ?.SoLuongBanKemLon || 0;
      return Number(dongTien) + Number(bonpas);
    },
    [dashboard.bonpasDaily, dashboard.daily, selectedEndDate]
  );
  const isKpiDateForBranch = useCallback(
    (date, branch) =>
      kpiRows.some(
        (configuration) =>
          configuration.ChiNhanh === branch &&
          date >= configuration.NgayBatDau &&
          date <= configuration.NgayKetThuc
      ),
    [kpiRows]
  );
  const seasonSales = useMemo(
    () =>
      sumBy(
        (dashboard.seasonDaily?.BanLe || []).filter((row) =>
          isKpiDateForBranch(row.Ngay, CAKE_BRANCHES.DONG_TIEN.code)
        ),
        (row) => row.ChiTiet?.BanhKemLon
      ) +
      sumBy(
        dashboard.bonpasSeason.filter((row) =>
          isKpiDateForBranch(row.Ngay, CAKE_BRANCHES.BONPAS.code)
        ),
        (row) => row.SoLuongBanKemLon
      ),
    [dashboard.bonpasSeason, dashboard.seasonDaily, isKpiDateForBranch]
  );
  const productionDayQuantity = Number(
    (dashboard.production?.data || []).find(
      (row) => row.Ngay === selectedEndDate
    )?.TongSoLuongBanhKemLon || 0
  );
  const productionSeasonQuantity = Number(
    dashboard.productionSeason?.summary?.totalLargeCakeQuantity || 0
  );

  const trendDates = useMemo(
    () => enumerateDateKeys(shiftDateKey(selectedEndDate, -6), selectedEndDate),
    [selectedEndDate]
  );
  const dailySalesMap = useMemo(
    () => {
      const result = new Map(
        (dashboard.daily?.BanLe || []).map((row) => [
          row.Ngay,
          Number(row.ChiTiet?.BanhKemLon || 0),
        ])
      );
      dashboard.bonpasDaily.forEach((row) =>
        result.set(
          row.Ngay,
          Number(result.get(row.Ngay) || 0) + Number(row.SoLuongBanKemLon || 0)
        )
      );
      return result;
    },
    [dashboard.bonpasDaily, dashboard.daily]
  );
  const trendChartData = {
    labels: trendDates.map((date) =>
      new Date(`${date}T00:00:00`).toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
      })
    ),
    datasets: [
      {
        label: "Bánh kem lớn bán",
        data: trendDates.map(
          (date) => dailySalesMap.get(date) || 0
        ),
        borderColor: "#ef6c3e",
        backgroundColor: "rgba(239, 108, 62, 0.16)",
        pointBackgroundColor: "#ef6c3e",
        fill: true,
        tension: 0.35,
      },
    ],
  };

  const seasonDates = useMemo(
    () => enumerateDateKeys(kpiSummary.startDate, kpiSummary.endDate),
    [kpiSummary.endDate, kpiSummary.startDate]
  );
  const seasonActualMap = useMemo(
    () => {
      const result = new Map(
        (dashboard.seasonDaily?.BanLe || [])
          .filter((row) =>
            isKpiDateForBranch(row.Ngay, CAKE_BRANCHES.DONG_TIEN.code)
          )
          .map((row) => [row.Ngay, Number(row.ChiTiet?.BanhKemLon || 0)])
      );
      dashboard.bonpasSeason
        .filter((row) =>
          isKpiDateForBranch(row.Ngay, CAKE_BRANCHES.BONPAS.code)
        )
        .forEach((row) =>
          result.set(
            row.Ngay,
            Number(result.get(row.Ngay) || 0) + Number(row.SoLuongBanKemLon || 0)
          )
        );
      return result;
    },
    [dashboard.bonpasSeason, dashboard.seasonDaily, isKpiDateForBranch]
  );
  const seasonTargetMap = useMemo(() => {
    const result = new Map();
    kpiRows.forEach((configuration) => {
      (configuration.NgayTargets || []).forEach((day) => {
        result.set(
          day.Ngay,
          Number(result.get(day.Ngay) || 0) + Number(day.KPIBanKemLon || 0)
        );
      });
    });
    return result;
  }, [kpiRows]);
  const productionSeasonMap = useMemo(
    () =>
      new Map(
        (dashboard.productionSeason?.data || []).map((row) => [
          row.Ngay,
          Number(row.TongSoLuongBanhKemLon || 0),
        ])
      ),
    [dashboard.productionSeason]
  );
  const productionTargetMap = useMemo(() => {
    const result = new Map();
    kpiRows
      .filter(
        (configuration) =>
          configuration.ChiNhanh === CAKE_BRANCHES.DONG_TIEN.code
      )
      .forEach((configuration) => {
        (configuration.NgayTargets || []).forEach((day) => {
          result.set(
            day.Ngay,
            Number(result.get(day.Ngay) || 0) +
              Number(day.KPISanXuatKemLon || 0)
          );
        });
      });
    return result;
  }, [kpiRows]);
  const seasonChartData = {
    labels: seasonDates.map((date) =>
      new Date(`${date}T00:00:00`).toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
      })
    ),
    datasets: [
      {
        type: "bar",
        label: "Bán thực tế",
        data: seasonDates.map(
          (date) => seasonActualMap.get(date) || 0
        ),
        backgroundColor: "rgba(37, 99, 235, 0.78)",
        borderRadius: 8,
      },
      {
        type: "line",
        label: "KPI bán ngày",
        data: seasonDates.map((date) => seasonTargetMap.get(date) || 0),
        borderColor: "#ef6c3e",
        backgroundColor: "#ef6c3e",
        tension: 0.25,
      },
      ...(selectedBranch !== CAKE_BRANCHES.BONPAS.code
        ? [
            {
              type: "bar",
              label: "Sản xuất thực tế",
              data: seasonDates.map(
                (date) => productionSeasonMap.get(date) || 0
              ),
              backgroundColor: "rgba(15, 159, 118, 0.72)",
              borderRadius: 8,
            },
            {
              type: "line",
              label: "KPI sản xuất ngày",
              data: seasonDates.map(
                (date) => productionTargetMap.get(date) || 0
              ),
              borderColor: "#0f9f76",
              backgroundColor: "#0f9f76",
              tension: 0.25,
            },
          ]
        : []),
    ],
  };

  const displayedTopStores = useMemo(() => {
    const stores = selectedBranch === CAKE_BRANCHES.BONPAS.code
      ? dashboard.bonpasTopStores
      : selectedBranch === CAKE_BRANCHES.DONG_TIEN.code
        ? dashboard.topStores
        : [...dashboard.topStores, ...dashboard.bonpasTopStores];
    return [...stores]
      .sort(
        (left, right) =>
          Number(right.ChiTiet?.BanhKemLon || 0) -
          Number(left.ChiTiet?.BanhKemLon || 0)
      )
      .slice(0, 15);
  }, [dashboard.bonpasTopStores, dashboard.topStores, selectedBranch]);
  const topSalesData = {
    labels: displayedTopStores.map(
      (store) =>
        store.ChiNhanh === CAKE_BRANCHES.BONPAS.code
          ? formatBonpasStoreLabel(store.TenVietTat)
          : store.TenVietTat || store.MaBravo || `CH${store.MaCH}`
    ),
    datasets: [
      {
        label: "Đã bán",
        data: displayedTopStores.map(
          (store) => store.ChiTiet?.BanhKemLon || 0
        ),
        backgroundColor: "#2563eb",
        borderRadius: 7,
      },
    ],
  };
  const displayedCategorySales =
    selectedBranch === CAKE_BRANCHES.BONPAS.code
      ? dashboard.bonpasCategorySales
      : dashboard.categorySales;
  const categorySalesData = {
    labels: displayedCategorySales.map((category) => category.TenNhom),
    datasets: [
      {
        label: "Số lượng bán",
        data: displayedCategorySales.map((category) => category.SoLuong || 0),
        backgroundColor: ["#ef6c3e", "#38bdf8", "#8b5cf6", "#0f9f76", "#f59e0b"],
        borderRadius: 8,
        maxBarThickness: 74,
      },
    ],
  };
  const topInventoryStores = [...inventorySummary.stores]
    .sort(
      (left, right) =>
        Number(right.ChiTiet?.BanhKemLon || 0) -
        Number(left.ChiTiet?.BanhKemLon || 0)
    )
    .slice(0, 15);
  const topInventoryData = {
    labels: topInventoryStores.map(
      (store) => store.TenVietTat || store.MaBravo || `CH${store.MaCH}`
    ),
    datasets: [
      {
        label: "Đang tồn",
        data: topInventoryStores.map((store) => store.ChiTiet?.BanhKemLon || 0),
        backgroundColor: "#0f9f76",
        borderRadius: 7,
      },
    ],
  };
  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${numberFormat.format(context.parsed.y)} cái`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };
  const branchLabel = CAKE_BRANCHES[selectedBranch]?.label || selectedBranch;

  return (
    <>
      <NavbarTrungThu />
      <main className="cake-dashboard">
        <header className="cake-dashboard-hero">
          <div>
            <p className="cake-eyebrow">Vận hành bánh kem · {branchLabel}</p>
            <h1>Dashboard bánh kem</h1>
            <p>Doanh số, bánh đặt, sản xuất và tồn kho từ các nguồn dữ liệu hiện hữu.</p>
          </div>
          <div className="cake-hero-total">
            <span>Bánh kem lớn bán trong ngày</span>
            <strong>{numberFormat.format(daySales)}</strong>
            <small>
              {selectedBranch === CAKE_BRANCHES.BONPAS.code
                ? `${selectedEndDate} · Dữ liệu tự động từ IPOS`
                : `${selectedEndDate} · ${branchLabel}`}
            </small>
          </div>
        </header>

        <section className="cake-filter-panel">
          <label>
            Đơn vị
            <select value={selectedBranch} onChange={(event) => handleBranchChange(event.target.value)}>
              <option value={CAKE_BRANCHES.ALL.code}>Tất cả</option>
              <option value={CAKE_BRANCHES.DONG_TIEN.code}>Đồng Tiến</option>
              <option value={CAKE_BRANCHES.BONPAS.code}>Bonpas</option>
            </select>
          </label>
          {filterMode === FILTER_MODES.DATE_RANGE ? (
            <>
              <label>
                Từ ngày
                <input type="date" value={selectedStartDate} max={selectedEndDate} onChange={(event) => setSelectedStartDate(event.target.value)} />
              </label>
              <label>
                Đến ngày
                <input type="date" value={selectedEndDate} min={selectedStartDate} onChange={(event) => setSelectedEndDate(event.target.value)} />
              </label>
              <button
                className="cake-secondary-button"
                onClick={() => {
                  setFilterMode(FILTER_MODES.SEASON);
                  setSelectedSeasonId("");
                }}
              >
                Chọn mùa vụ
              </button>
            </>
          ) : (
            <>
              <label className="cake-season-filter">
                Mùa vụ
                <select
                  value={selectedSeasonId}
                  onChange={(event) => handleSeasonChange(event.target.value)}
                >
                  <option value="">Chọn mùa vụ cần xem</option>
                  {availableSeasons.map((configuration) => (
                    <option key={configuration.ID} value={configuration.ID}>
                      {configuration.TenMuaVu} · {CAKE_BRANCHES[configuration.ChiNhanh]?.label || configuration.ChiNhanh} · {configuration.NgayBatDau} → {configuration.NgayKetThuc}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="cake-secondary-button"
                onClick={() => {
                  setFilterMode(FILTER_MODES.DATE_RANGE);
                  setSelectedSeasonId("");
                }}
              >
                Chọn khoảng ngày
              </button>
            </>
          )}
          <button className="cake-secondary-button" onClick={() => setRefreshKey((value) => value + 1)}>
            Làm mới
          </button>
          {canManageKpi ? (
            <button className="cake-primary-button" onClick={() => setSettingsOpen(true)}>
              Cấu hình KPI
            </button>
          ) : null}
        </section>

        {error ? <div className="cake-error-banner">{error}</div> : null}
        {loading ? <div className="cake-loading-bar"><span /></div> : null}

        <section className={`cake-dashboard-row cake-circle-row ${selectedBranch === CAKE_BRANCHES.BONPAS.code ? "without-inventory" : ""}`}>
          <CircleMetric
            eyebrow={kpiSummary.seasonNames.join(" · ") || "Mùa vụ"}
            title="Tiến độ KPI mùa"
            value={seasonSales}
            target={kpiSummary.seasonTarget}
            color="#2563eb"
          />
          <CircleMetric
            eyebrow={selectedEndDate}
            title="Tiến độ KPI ngày"
            value={daySales}
            target={kpiSummary.dailyTarget}
            color="#ef6c3e"
          />
          {selectedBranch !== CAKE_BRANCHES.BONPAS.code ? (
            <CircleMetric
              eyebrow="Tồn hiện tại · Đồng Tiến"
              title="Tồn kho"
              value={inventorySummary.totalLargeCakes}
              color="#0f9f76"
              note={`${selectedStoreIds.length} cửa hàng Đồng Tiến`}
            />
          ) : null}
        </section>

        <section className="cake-dashboard-row cake-secondary-row">
          <CircleMetric
            eyebrow="Đơn đã xác nhận · Đồng Tiến"
            title="Số lượng bánh kem đặt"
            value={dashboard.orders?.SoLuong || 0}
            color="#8b5cf6"
            note={selectedBranch === CAKE_BRANCHES.BONPAS.code ? "Bonpas chưa có API bánh đặt" : "Số thực tế, không áp KPI"}
          />
          <article className="cake-chart-card">
            <div className="cake-chart-heading">
              <div>
                <p className="cake-eyebrow">Số lượng bán · {selectedBranch === CAKE_BRANCHES.BONPAS.code ? "Bonpas" : "Đồng Tiến"}</p>
                <h2>Cơ cấu các loại bánh kem</h2>
              </div>
              <span className="cake-period-chip">{selectedStartDate} → {selectedEndDate}</span>
            </div>
            <div className="cake-chart-canvas cake-chart-canvas-large">
              <Bar data={categorySalesData} options={commonChartOptions} />
            </div>
          </article>
        </section>

        <section className="cake-dashboard-row cake-single-row">
          <article className="cake-chart-card">
            <div className="cake-chart-heading"><div><p className="cake-eyebrow">7 ngày gần nhất</p><h2>Xu hướng bán bánh kem lớn</h2></div></div>
            <div className="cake-chart-canvas"><Line data={trendChartData} options={commonChartOptions} /></div>
          </article>
        </section>

        <section className="cake-dashboard-row cake-single-row">
          {kpiSummary.hasKpi || kpiSummary.hasProductionKpi ? (
            <article className="cake-chart-card">
              <div className="cake-chart-heading">
                <div>
                  <p className="cake-eyebrow">KPI mùa vụ · tiến trình ngày</p>
                  <h2>Bán và sản xuất bánh kem lớn</h2>
                  <p className="cake-kpi-summary-line">
                    Bán mùa: {numberFormat.format(seasonSales)} / {numberFormat.format(kpiSummary.seasonTarget || 0)}
                    {selectedBranch !== CAKE_BRANCHES.BONPAS.code ? ` · Sản xuất mùa: ${numberFormat.format(productionSeasonQuantity)} / ${numberFormat.format(kpiSummary.productionSeasonTarget || 0)} · Hôm nay: ${numberFormat.format(productionDayQuantity)} / ${numberFormat.format(kpiSummary.productionDailyTarget || 0)}` : ""}
                  </p>
                </div>
                <span className="cake-period-chip">{kpiSummary.startDate} → {kpiSummary.endDate}</span>
              </div>
              <div className="cake-chart-canvas cake-chart-canvas-large"><Bar data={seasonChartData} options={commonChartOptions} /></div>
            </article>
          ) : (
            <article className="cake-chart-card cake-empty-kpi">
              <div><p className="cake-eyebrow">KPI mùa vụ</p><h2>Chưa có KPI cho {branchLabel} tại ngày {selectedEndDate}</h2><p>Số thực tế vẫn được hiển thị; quản trị viên có thể tạo KPI mà không cần sửa code.</p></div>
              {canManageKpi ? <button className="cake-primary-button" onClick={() => setSettingsOpen(true)}>Tạo KPI mùa vụ</button> : null}
            </article>
          )}
        </section>

        <section className={`cake-dashboard-row cake-top-row ${selectedBranch === CAKE_BRANCHES.BONPAS.code ? "without-inventory" : ""}`}>
          <article className="cake-chart-card">
            <div className="cake-chart-heading"><div><p className="cake-eyebrow">Top 15 cửa hàng · {branchLabel}</p><h2>Bán bánh kem lớn</h2></div></div>
            <div className="cake-chart-canvas cake-chart-canvas-large"><Bar data={topSalesData} options={commonChartOptions} /></div>
          </article>
          {selectedBranch !== CAKE_BRANCHES.BONPAS.code ? (
            <article className="cake-chart-card">
              <div className="cake-chart-heading"><div><p className="cake-eyebrow">Top 15 cửa hàng · Đồng Tiến</p><h2>Tồn bánh kem lớn</h2></div></div>
              <div className="cake-chart-canvas cake-chart-canvas-large"><Bar data={topInventoryData} options={commonChartOptions} /></div>
            </article>
          ) : null}
        </section>
      </main>

      <CakeKpiSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={() => setRefreshKey((value) => value + 1)} />
    </>
  );
};

export default DashboardBkemCustomByMockData;
